import React, { createContext, useCallback, useContext, useRef } from 'react';

import { throwInDev } from '@work4all/utils';

export type OnCloseCallback = () => void;

interface OnCloseContextType {
  registerOnClose: (callback: OnCloseCallback) => () => void;
  executeAllCallbacks: () => void;
}

const OnCloseContext = createContext<OnCloseContextType | null>(null);

const noopUseRegisterOnClose = () => {
  throwInDev('useRegisterOnClose must be used within an OnCloseProvider');
  return () => {};
};

export const useRegisterOnClose = () => {
  const context = useContext(OnCloseContext);
  if (!context) {
    // Return a no-op function when context is not available
    return noopUseRegisterOnClose;
  }
  return context.registerOnClose;
};

// Hook to use the onClose functionality
export const useOnClose = (
  callback: OnCloseCallback,
  deps: React.DependencyList = []
) => {
  const context = useContext(OnCloseContext);

  // Use ref to store the current callback to avoid stale closures
  const callbackRef = useRef(callback);
  callbackRef.current = callback;

  React.useEffect(() => {
    // If no context, just skip the onClose functionality
    if (!context) {
      throwInDev('useOnClose must be used within an OnCloseProvider');
      return;
    }

    const { registerOnClose } = context;
    const stableCallback = () => callbackRef.current();
    const unregister = registerOnClose(stableCallback);

    return unregister;
  }, deps); // eslint-disable-line react-hooks/exhaustive-deps
};

// Higher-Order Component that provides onClose context and enhanced close functionality
export function withOnCloseProvider<T extends { close?: () => void }>(
  Component: React.ComponentType<T>
): React.ComponentType<T> {
  const WrappedComponent: React.FC<T> = (props) => {
    // Use ref to store callbacks to avoid re-renders when callbacks change
    const callbacksRef = useRef<Set<OnCloseCallback>>(new Set());
    const closeRef = useRef(props.close);

    // Keep ref updated with the latest props.close
    React.useEffect(() => {
      closeRef.current = props.close;
    }, [props.close]);

    const registerOnClose = useCallback((callback: OnCloseCallback) => {
      callbacksRef.current.add(callback);

      // Return unregister function
      return () => {
        callbacksRef.current.delete(callback);
      };
    }, []);

    const executeAllCallbacks = useCallback(() => {
      // Execute all registered callbacks
      callbacksRef.current.forEach((callback) => {
        try {
          callback();
        } catch (error) {
          console.error('Error executing onClose callback:', error);
        }
      });
    }, []);

    // Enhanced close function that executes callbacks first
    const handleClose = useCallback(() => {
      // Execute all registered onClose callbacks first
      executeAllCallbacks();
      // Use latest version of close
      if (closeRef.current) {
        closeRef.current();
      }
    }, [executeAllCallbacks]);

    const contextValue: OnCloseContextType = {
      registerOnClose,
      executeAllCallbacks,
    };

    // Pass the enhanced close function to the wrapped component
    const enhancedProps = {
      ...props,
      close: handleClose,
    } as T;

    return (
      <OnCloseContext.Provider value={contextValue}>
        <Component {...enhancedProps} />
      </OnCloseContext.Provider>
    );
  };

  WrappedComponent.displayName = `withOnCloseProvider(${
    Component.displayName || Component.name
  })`;

  return WrappedComponent;
}
